export const getSessionId = (session) =>
  session?.conversationsId ||
  session?.conversationId ||
  session?.ConversationId ||
  session?.ConversationsId ||
  session?.id ||
  null;

export const isTemporaryConversationId = (conversationId) =>
  String(conversationId ?? "").startsWith("chat-");

export const extractConversationId = (payload) => {
  if (!payload) return null;

  if (Array.isArray(payload)) {
    return extractConversationId(payload[0]);
  }

  const directId =
    payload?.conversationId ??
    payload?.conversationsId ??
    payload?.ConversationId ??
    payload?.ConversationsId ??
    payload?.id ??
    null;

  if (directId && !isTemporaryConversationId(directId)) {
    return directId;
  }

  return null;
};

const inferMediaType = (contentText, explicitMediaType) => {
  const mediaType = String(explicitMediaType ?? "").toLowerCase();
  if (mediaType === "image" || mediaType === "video" || mediaType === "content") {
    return mediaType;
  }

  const text = String(contentText ?? "");
  if (/\.(mp4|webm)(\?|$)/i.test(text) || text.includes("video/")) {
    return "video";
  }
  if (
    /\.(png|jpe?g|gif|webp)(\?|$)/i.test(text) ||
    text.startsWith("data:image") ||
    text.startsWith("blob:")
  ) {
    return "image";
  }
  if (text.startsWith("{") || text.startsWith("[")) {
    return "content";
  }
  return "image";
};

const inferMediaTypeFromFilename = (filename) => {
  const name = String(filename ?? "").toLowerCase();
  if (/\.(mp4|webm|mov)(\?|$)/.test(name)) return "video";
  if (/\.(png|jpe?g|gif|webp)(\?|$)/.test(name)) return "image";
  return null;
};

const parseMaybeJson = (value) => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed || (trimmed[0] !== "{" && trimmed[0] !== "[")) return null;
  try {
    return JSON.parse(trimmed);
  } catch {
    return null;
  }
};

const addAttachmentName = (names, value) => {
  const name = String(value ?? "").trim();
  if (!name || name === "[object Object]" || names.includes(name)) return;
  names.push(name);
};

const collectAttachmentNames = (names, value) => {
  if (value == null || value === "") return;

  if (typeof value === "string") {
    const parsed = parseMaybeJson(value);
    if (parsed != null && typeof parsed !== "string") {
      collectAttachmentNames(names, parsed);
      return;
    }
    addAttachmentName(names, value);
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item) => collectAttachmentNames(names, item));
    return;
  }

  if (typeof value === "object") {
    addAttachmentName(
      names,
      value.name ??
        value.fileName ??
        value.FileName ??
        value.attachmentName ??
        value.attachmentFileName,
    );
  }
};

export const resolveGenerateAttachmentNames = (msg) => {
  const names = [];
  addAttachmentName(names, msg?.attachmentName);
  addAttachmentName(names, msg?.attachmentFileName);
  collectAttachmentNames(names, msg?.attachment);
  collectAttachmentNames(names, msg?.attachments);
  addAttachmentName(names, msg?.fileName);
  addAttachmentName(names, msg?.FileName);
  return names;
};

export const mapApiMessageToGenerateMessage = (msg) => {
  const role = String(msg?.role ?? "").toLowerCase();
  const contentText = String(msg?.contentText ?? msg?.content ?? "").trim();
  const contentMedia = String(msg?.contentMedia ?? msg?.ContentMedia ?? "").trim();
  const contentMediaBaseUrl = String(
    msg?.contentMediaBaseUrl ?? msg?.ContentMediaBaseUrl ?? "",
  ).trim();
  const mediaType = inferMediaType(
    contentText,
    msg?.mediaType ?? inferMediaTypeFromFilename(contentMedia),
  );
  const persistedMessageId =
    msg?.messagesId ??
    msg?.messageId ??
    msg?.MessagesId ??
    msg?.MessageId ??
    msg?.id ??
    null;

  if (role === "user") {
    const attachmentNames = resolveGenerateAttachmentNames(msg);
    return {
      type: "user",
      content: contentText,
      messagesId: persistedMessageId,
      ...(attachmentNames.length
        ? {
            attachmentName: attachmentNames[0],
            attachmentNames,
          }
        : {}),
    };
  }

  if (
    (mediaType === "image" || mediaType === "video") &&
    contentMedia &&
    contentMediaBaseUrl
  ) {
    return {
      type: "bot",
      content: "",
      isImage: mediaType === "image",
      isVideo: mediaType === "video",
      originalPrompt: msg?.originalPrompt ?? contentText,
      loadingMedia: true,
      pendingMediaView: {
        contentMedia,
        contentMediaBaseUrl,
        mediaType,
      },
    };
  }

  if (mediaType === "content") {
    try {
      const parsed = JSON.parse(contentText);
      const contentData =
        parsed?.content && typeof parsed.content === "object"
          ? parsed.content
          : parsed;
      if (contentData && typeof contentData === "object") {
        return { type: "bot", content: contentData, isContent: true };
      }
    } catch {
      // Fall through to text display.
    }
  }

  if (
    mediaType === "video" ||
    /\.(mp4|webm)(\?|$)/i.test(contentText) ||
    contentText.startsWith("data:video")
  ) {
    return {
      type: "bot",
      content: contentText,
      isVideo: true,
      originalPrompt: msg?.originalPrompt,
    };
  }

  if (
    mediaType === "image" ||
    contentText.startsWith("http") ||
    contentText.startsWith("data:image") ||
    contentText.startsWith("blob:")
  ) {
    return {
      type: "bot",
      content: contentText,
      isImage: true,
      originalPrompt: msg?.originalPrompt,
    };
  }

  return { type: "bot", content: contentText || "No response" };
};

export const mapApiMessagesToGenerateMessages = (apiMessages) => {
  if (!Array.isArray(apiMessages)) return [];

  return [...apiMessages]
    .sort(
      (a, b) =>
        new Date(a?.createdAt || 0).getTime() -
        new Date(b?.createdAt || 0).getTime(),
    )
    .map(mapApiMessageToGenerateMessage);
};
